import { tzOpts } from "@/lib/tz";
import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Megaphone, Plus, Pencil, Trash2, Play, Pause, Users, Send, Repeat, CalendarClock,
  Image as ImageIcon, X, Upload, Search, UserCheck, Clock, Copy, AlertTriangle,
} from "lucide-react";

const WEEKDAYS = ["Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado", "Domingo"];

const TYPE_STYLES = {
  marketing: "bg-fuchsia-500/15 text-fuchsia-300 border border-fuchsia-500/30",
  maintenance: "bg-amber-500/15 text-amber-300 border border-amber-500/30",
  other: "bg-slate-500/15 text-slate-300 border border-slate-500/30",
};
const TYPE_LABEL = { marketing: "Marketing", maintenance: "Manutenção", other: "Outro" };

const STATUS_STYLES = {
  scheduled: "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30",
  running: "bg-sky-500/15 text-sky-300 border border-sky-500/30",
  paused: "bg-slate-500/15 text-slate-400 border border-slate-500/30",
  interrupted: "bg-amber-500/15 text-amber-300 border border-amber-500/30",
  cancelled: "bg-slate-500/15 text-slate-500 border border-slate-500/30",
  sent: "bg-slate-500/15 text-slate-400 border border-slate-500/30",
};
const STATUS_LABEL = { scheduled: "Agendada", running: "Enviando…", paused: "Pausada", interrupted: "Interrompida", cancelled: "Cancelada", sent: "Enviada" };

const EMPTY_FORM = {
  name: "", message: "", message_type: "marketing", schedule_type: "once",
  send_at_date: "", send_at_time: "09:00",
  recurrence_freq: "daily", recurrence_time: "09:00", recurrence_weekday: 0, recurrence_day_of_month: 1,
  image_data_uri: "",
  target_type: "all", selectedClients: [], // [{login, nome, celular}]
};

const MAX_IMAGE_BYTES = 2 * 1024 * 1024; // 2MB

function scheduleSummary(c) {
  if (c.schedule_type === "once") {
    return c.send_at ? new Date(c.send_at).toLocaleString("pt-BR", tzOpts()) : "—";
  }
  const rec = c.recurrence || {};
  if (rec.freq === "daily") return `Todo dia às ${rec.time}`;
  if (rec.freq === "weekly") return `Toda ${WEEKDAYS[rec.weekday] || "?"} às ${rec.time}`;
  if (rec.freq === "monthly") return `Dia ${rec.day_of_month} de cada mês às ${rec.time}`;
  return "—";
}

function nextRunInfo(c) {
  if (!c.next_run_at || c.status !== "scheduled") return null;
  const d = new Date(c.next_run_at);
  const diffMs = d.getTime() - Date.now();
  const diffMin = Math.round(diffMs / 60000);
  let relative;
  if (diffMin < 0) relative = "atrasado";
  else if (diffMin < 1) relative = "em instantes";
  else if (diffMin < 60) relative = `em ${diffMin} min`;
  else if (diffMin < 24 * 60) relative = `em ${Math.round(diffMin / 60)}h`;
  else relative = `em ${Math.round(diffMin / (24 * 60))}d`;
  return { label: d.toLocaleString("pt-BR", tzOpts()), relative, soon: diffMin >= 0 && diffMin < 60 };
}

/** Ordena: agendadas com envio mais próximo primeiro, pausadas depois, enviadas por último. */
function sortCampaigns(list) {
  const rank = (c) => (c.status === "interrupted" ? -1 : c.status === "scheduled" ? 0 : c.status === "paused" ? 1 : 2);
  return [...list].sort((a, b) => {
    const r = rank(a) - rank(b);
    if (r !== 0) return r;
    if (a.status === "scheduled" && b.status === "scheduled") {
      return new Date(a.next_run_at || 0) - new Date(b.next_run_at || 0);
    }
    return new Date(b.created_at || 0) - new Date(a.created_at || 0);
  });
}

export default function Marketing() {
  const [campaigns, setCampaigns] = useState([]);
  const [audience, setAudience] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const imageInputRef = useRef(null);

  // -------- busca de clientes pro seletor (chips) --------
  const [clientSearch, setClientSearch] = useState("");
  const [clientResults, setClientResults] = useState([]);
  const [searchingClients, setSearchingClients] = useState(false);
  const searchDebounce = useRef(null);
  const searchReqId = useRef(0);

  const load = async () => {
    try {
      const [{ data: cs }, { data: aud }] = await Promise.all([
        api.get("/marketing/campaigns"),
        api.get("/marketing/audience-count"),
      ]);
      setCampaigns(cs);
      setAudience(aud.count);
    } catch (ex) {
      toast.error(formatApiError(ex));
    }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setClientSearch("");
    setClientResults([]);
    setFormOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    const sendAt = c.send_at ? new Date(c.send_at) : null;
    setForm({
      name: c.name,
      message: c.message,
      message_type: c.message_type,
      schedule_type: c.schedule_type,
      send_at_date: sendAt ? sendAt.toISOString().slice(0, 10) : "",
      send_at_time: sendAt ? sendAt.toTimeString().slice(0, 5) : "09:00",
      recurrence_freq: c.recurrence?.freq || "daily",
      recurrence_time: c.recurrence?.time || "09:00",
      recurrence_weekday: c.recurrence?.weekday ?? 0,
      recurrence_day_of_month: c.recurrence?.day_of_month ?? 1,
      image_data_uri: c.image_data_uri || "",
      target_type: c.target_type || "all",
      selectedClients: c.target_clients && c.target_clients.length
        ? c.target_clients
        : (c.target_logins || []).map((login) => ({ login, nome: login, celular: "" })),
    });
    setClientSearch("");
    setClientResults([]);
    setFormOpen(true);
  };

  // Duplicar = abrir o formulario de NOVA campanha ja preenchido com o conteudo desta.
  // Como e uma campanha nova (outro id), todo mundo da lista volta a poder receber.
  const openDuplicate = (c) => {
    openEdit(c);
    setEditing(null);
    setForm((f) => ({ ...f, name: `${c.name} (cópia)`, send_at_date: "", send_at_time: "09:00" }));
    toast.info("Cópia aberta: escolha a data e o horário e salve para agendar como nova campanha.");
  };

  const pickImage = () => imageInputRef.current?.click();

  const onImageSelected = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Selecione um arquivo de imagem");
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      toast.error("Imagem muito grande (máx. 2MB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, image_data_uri: reader.result }));
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const removeImage = () => setForm((f) => ({ ...f, image_data_uri: "" }));

  const onClientSearchChange = (value) => {
    setClientSearch(value);
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    if (!value.trim()) {
      setClientResults([]);
      return;
    }
    searchDebounce.current = setTimeout(async () => {
      const id = ++searchReqId.current;
      setSearchingClients(true);
      try {
        const { data } = await api.get("/clients", { params: { search: value.trim(), limit: 10 } });
        if (id === searchReqId.current) setClientResults(data?.items || []);
      } catch {
        // silencioso - busca de conveniencia, nao precisa incomodar com toast a cada digitada
      } finally {
        if (id === searchReqId.current) setSearchingClients(false);
      }
    }, 350);
  };

  const addClient = (c) => {
    setForm((f) => {
      if (f.selectedClients.some((x) => x.login === c.login)) return f;
      return { ...f, selectedClients: [...f.selectedClients, { login: c.login, nome: c.nome, celular: c.celular }] };
    });
    setClientSearch("");
    setClientResults([]);
  };

  const removeClient = (login) => {
    setForm((f) => ({ ...f, selectedClients: f.selectedClients.filter((c) => c.login !== login) }));
  };

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Informe o nome da campanha");
    if (!form.message.trim()) return toast.error("Informe o texto da mensagem");
    if (form.target_type === "selected" && form.selectedClients.length === 0) {
      return toast.error("Selecione ao menos um cliente, ou mude pra 'Todos os clientes ativos'");
    }
    setSaving(true);
    try {
      const body = {
        name: form.name.trim(),
        message: form.message.trim(),
        message_type: form.message_type,
        schedule_type: form.schedule_type,
        image_data_uri: form.image_data_uri || null,
        target_type: form.target_type,
        target_logins: form.target_type === "selected" ? form.selectedClients.map((c) => c.login) : [],
        target_clients: form.target_type === "selected" ? form.selectedClients : [],
      };
      if (form.schedule_type === "once") {
        if (!form.send_at_date) { toast.error("Escolha a data do envio"); setSaving(false); return; }
        body.send_at = `${form.send_at_date}T${form.send_at_time}:00`;
      } else {
        body.recurrence = {
          freq: form.recurrence_freq,
          time: form.recurrence_time,
          weekday: parseInt(form.recurrence_weekday, 10),
          day_of_month: parseInt(form.recurrence_day_of_month, 10),
        };
      }
      if (editing) {
        await api.put(`/marketing/campaigns/${editing._id}`, body);
        toast.success("Campanha atualizada");
      } else {
        await api.post("/marketing/campaigns", body);
        toast.success("Campanha agendada");
      }
      setFormOpen(false);
      load();
    } catch (ex) {
      toast.error(formatApiError(ex));
    } finally {
      setSaving(false);
    }
  };

  const togglePause = async (c) => {
    try {
      const { data } = await api.post(`/marketing/campaigns/${c._id}/pause`);
      toast.success(data.status === "paused" ? "Campanha pausada" : "Campanha reativada");
      load();
    } catch (ex) { toast.error(formatApiError(ex)); }
  };

  const resumeCampaign = async (c) => {
    try {
      await api.post(`/marketing/campaigns/${c._id}/resume`);
      toast.success("Campanha retomada - continua de onde parou");
      load();
    } catch (ex) { toast.error(formatApiError(ex)); }
  };

  const cancelRemaining = async (c) => {
    if (!window.confirm(`Cancelar os envios restantes de "${c.name}"? Quem já recebeu não é afetado.`)) return;
    try {
      await api.post(`/marketing/campaigns/${c._id}/cancel`);
      toast.success("Envios restantes cancelados");
      load();
    } catch (ex) { toast.error(formatApiError(ex)); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Excluir a campanha "${c.name}"?`)) return;
    try {
      await api.delete(`/marketing/campaigns/${c._id}`);
      toast.success("Campanha excluída");
      load();
    } catch (ex) { toast.error(formatApiError(ex)); }
  };

  const sendNow = async (c) => {
    const n = c.target_type === "selected" ? (c.target_logins || []).length : (audience ?? "...");
    if (!window.confirm(`Enviar "${c.name}" agora mesmo, pra ${n} cliente(s)?`)) return;
    try {
      await api.post(`/marketing/campaigns/${c._id}/send-now`);
      toast.success("Envio iniciado em segundo plano");
      load();
    } catch (ex) { toast.error(formatApiError(ex)); }
  };

  return (
    <div className="space-y-8">
      <header className="border-b border-slate-800 pb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-400">Marketing</div>
          <h1 className="font-outfit text-4xl font-bold tracking-tight text-white mt-1">Campanhas</h1>
          <p className="text-sm text-slate-500 mt-2">
            Mensagens em massa pros seus clientes — pra todos os ativos ou só pra quem você escolher — avisos de
            manutenção, promoções e campanhas de marketing, únicas ou recorrentes.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {audience != null && (
            <div className="text-sm text-slate-400 flex items-center gap-1.5">
              <Users className="w-4 h-4 text-emerald-400" /> {audience} clientes ativos
            </div>
          )}
          <Button data-testid="open-create-campaign-btn" onClick={openCreate} className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold">
            <Plus className="w-4 h-4 mr-1" /> Nova campanha
          </Button>
        </div>
      </header>

      <Card className="bg-slate-900/50 border-slate-800 p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-800">
            <tr>
              <th className="text-left py-3 px-4 w-12"></th>
              <th className="text-left py-3 px-4">Campanha</th>
              <th className="text-left py-3 px-4">Tipo</th>
              <th className="text-left py-3 px-4">Público</th>
              <th className="text-left py-3 px-4">Agendamento</th>
              <th className="text-left py-3 px-4">Próximo envio</th>
              <th className="text-left py-3 px-4">Status</th>
              <th className="text-left py-3 px-4">Último envio</th>
              <th className="text-left py-3 px-4">Ações</th>
            </tr>
          </thead>
          <tbody data-testid="campaigns-tbody">
            {sortCampaigns(campaigns).map((c) => (
              <tr key={c._id} className="border-b border-slate-800/60 hover:bg-slate-800/40">
                <td className="py-2 px-4">
                  {c.image_data_uri ? (
                    <img src={c.image_data_uri} alt="" className="w-8 h-8 rounded object-cover border border-slate-700" />
                  ) : (
                    <div className="w-8 h-8 rounded bg-slate-800 flex items-center justify-center">
                      <ImageIcon className="w-3.5 h-3.5 text-slate-600" />
                    </div>
                  )}
                </td>
                <td className="py-3 px-4 text-white">{c.name}</td>
                <td className="py-3 px-4"><Badge className={TYPE_STYLES[c.message_type] || TYPE_STYLES.other}>{TYPE_LABEL[c.message_type] || c.message_type}</Badge></td>
                <td className="py-3 px-4 text-xs text-slate-400">
                  {c.target_type === "selected" ? (
                    <span className="flex items-center gap-1.5">
                      <UserCheck className="w-3.5 h-3.5 text-sky-400" /> {(c.target_logins || []).length} selecionado(s)
                    </span>
                  ) : (
                    <span className="flex items-center gap-1.5">
                      <Users className="w-3.5 h-3.5 text-slate-500" /> Todos os ativos
                    </span>
                  )}
                </td>
                <td className="py-3 px-4 text-slate-400 text-xs">
                  <div className="flex items-center gap-1.5">
                    {c.schedule_type === "recurring" ? <Repeat className="w-3.5 h-3.5 text-slate-500" /> : <CalendarClock className="w-3.5 h-3.5 text-slate-500" />}
                    {scheduleSummary(c)}
                  </div>
                </td>
                <td className="py-3 px-4 text-xs">
                  {(() => {
                    const info = nextRunInfo(c);
                    if (!info) return <span className="text-slate-600">—</span>;
                    return (
                      <div className={`flex items-center gap-1.5 ${info.soon ? "text-emerald-300" : "text-slate-400"}`}>
                        <Clock className="w-3.5 h-3.5" />
                        <div>
                          <div>{info.label}</div>
                          <div className="text-[10px] opacity-70">{info.relative}</div>
                        </div>
                      </div>
                    );
                  })()}
                </td>
                <td className="py-3 px-4">
                  <Badge className={STATUS_STYLES[c.status] || STATUS_STYLES.sent}>{STATUS_LABEL[c.status] || c.status}</Badge>
                  {c.last_run_at && c.last_result && c.last_result.total === 0 && c.last_result.sent === 0
                    && c.last_result.complete !== false && c.status !== "interrupted" && (
                    <div data-testid={`campaign-nobody-${c._id}`} className="text-[11px] text-amber-300 mt-1 max-w-[240px] flex gap-1">
                      <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                      <span>
                        Ninguém recebeu
                        {c.last_result.already_received
                          ? `: os ${c.last_result.already_received} clientes já tinham recebido esta campanha`
                          : ": a lista estava vazia ou todos já tinham recebido esta campanha"}
                        . Use <b>Duplicar</b> para enviar de novo.
                      </span>
                    </div>
                  )}
                  {c.status === "interrupted" && (
                    <div className="text-[11px] text-amber-300/80 mt-1 max-w-[220px]">
                      WhatsApp caiu no meio do envio{c.last_result ? ` (${c.last_result.sent}/${c.last_result.total} enviadas)` : ""}. Não retoma sozinha.
                    </div>
                  )}
                </td>
                <td className="py-3 px-4 font-mono-jb text-xs text-slate-500">
                  {c.last_run_at ? (
                    <span title={JSON.stringify(c.last_result)}>
                      {new Date(c.last_run_at).toLocaleString("pt-BR", tzOpts())}
                      {c.last_result && ` · ${c.last_result.sent}/${c.last_result.total} enviadas`}
                    </span>
                  ) : "—"}
                </td>
                <td className="py-3 px-4">
                  <div className="flex items-center gap-2">
                    {c.status === "interrupted" && (
                      <button
                        data-testid={`resume-campaign-${c._id}`}
                        onClick={() => resumeCampaign(c)}
                        title="Retomar de onde parou"
                        className="text-amber-300 hover:text-emerald-300 p-1"
                      >
                        <Play className="w-4 h-4" />
                      </button>
                    )}
                    {(c.status === "interrupted" || c.status === "running") && (
                      <button
                        data-testid={`cancel-campaign-${c._id}`}
                        onClick={() => cancelRemaining(c)}
                        title="Cancelar envios restantes"
                        className="text-slate-400 hover:text-red-400 p-1"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                    {(c.status === "scheduled" || c.status === "paused") && (
                      <button
                        data-testid={`pause-campaign-${c._id}`}
                        onClick={() => togglePause(c)}
                        title={c.status === "paused" ? "Reativar" : "Pausar"}
                        className="text-slate-400 hover:text-emerald-300 p-1"
                      >
                        {c.status === "paused" ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
                      </button>
                    )}
                    <button
                      data-testid={`send-now-campaign-${c._id}`}
                      onClick={() => sendNow(c)}
                      title="Enviar agora"
                      className="text-slate-400 hover:text-emerald-300 p-1"
                    >
                      <Send className="w-4 h-4" />
                    </button>
                    <button
                      data-testid={`duplicate-campaign-${c._id}`}
                      onClick={() => openDuplicate(c)}
                      title="Duplicar (cria uma nova campanha com este conteúdo)"
                      className="text-slate-400 hover:text-emerald-300 p-1"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                    <button onClick={() => openEdit(c)} title="Editar" className="text-slate-400 hover:text-emerald-300 p-1">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => remove(c)} title="Excluir" className="text-slate-400 hover:text-red-400 p-1">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {campaigns.length === 0 && (
              <tr><td colSpan={9} className="text-center py-10 text-slate-500">
                <Megaphone className="w-6 h-6 mx-auto mb-2 text-slate-700" />
                Nenhuma campanha ainda. Crie a primeira acima.
              </td></tr>
            )}
          </tbody>
        </table>
      </Card>

      {/* -------- Dialog: nova/editar campanha -------- */}
      <Dialog open={formOpen} onOpenChange={(v) => !v && setFormOpen(false)}>
        <DialogContent className="bg-slate-950 border-slate-800 text-slate-200 max-w-lg max-h-[90vh] overflow-y-auto" data-testid="campaign-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit text-white flex items-center gap-2">
              <Megaphone className="w-4 h-4 text-emerald-400" /> {editing ? "Editar campanha" : "Nova campanha"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-slate-400 text-xs uppercase tracking-[0.15em]">Nome (só pra organização interna)</Label>
              <Input
                data-testid="campaign-name-input"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Ex.: Manutenção 15/09, Promoção fibra 500MB"
                className="mt-1 bg-slate-950 border-slate-800 text-white"
              />
            </div>
            <div>
              <Label className="text-slate-400 text-xs uppercase tracking-[0.15em]">Tipo</Label>
              <Select value={form.message_type} onValueChange={(v) => setForm((f) => ({ ...f, message_type: v }))}>
                <SelectTrigger data-testid="campaign-type-select" className="mt-1 bg-slate-950 border-slate-800 text-white h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700 text-slate-200">
                  <SelectItem value="marketing">Marketing / promoção</SelectItem>
                  <SelectItem value="maintenance">Aviso de manutenção</SelectItem>
                  <SelectItem value="other">Outro</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* -------- Público -------- */}
            <div>
              <Label className="text-slate-400 text-xs uppercase tracking-[0.15em]">Público</Label>
              <Select value={form.target_type} onValueChange={(v) => setForm((f) => ({ ...f, target_type: v }))}>
                <SelectTrigger data-testid="campaign-target-type" className="mt-1 bg-slate-950 border-slate-800 text-white h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700 text-slate-200">
                  <SelectItem value="all">Todos os clientes ativos {audience != null && `(${audience})`}</SelectItem>
                  <SelectItem value="selected">Clientes selecionados</SelectItem>
                </SelectContent>
              </Select>

              {form.target_type === "selected" && (
                <div className="mt-2 space-y-2 border border-slate-800 rounded-md p-3">
                  <div className="relative">
                    <Search className="w-3.5 h-3.5 text-slate-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
                    <Input
                      data-testid="campaign-client-search"
                      value={clientSearch}
                      onChange={(e) => onClientSearchChange(e.target.value)}
                      placeholder="Buscar por nome, login, CPF ou celular…"
                      className="bg-slate-950 border-slate-800 text-white pl-8 h-9"
                    />
                    {(clientResults.length > 0 || searchingClients) && (
                      <div className="absolute z-10 mt-1 w-full bg-slate-900 border border-slate-700 rounded-md shadow-xl max-h-48 overflow-y-auto">
                        {searchingClients && <div className="px-3 py-2 text-xs text-slate-500">Buscando…</div>}
                        {!searchingClients && clientResults.map((c) => (
                          <button
                            key={c.login}
                            data-testid={`client-result-${c.login}`}
                            onClick={() => addClient(c)}
                            className="w-full text-left px-3 py-2 text-xs hover:bg-slate-800 flex items-center justify-between gap-2"
                          >
                            <span className="text-slate-200">{c.nome}</span>
                            <span className="text-slate-500 font-mono-jb">{c.login}</span>
                          </button>
                        ))}
                        {!searchingClients && clientResults.length === 0 && (
                          <div className="px-3 py-2 text-xs text-slate-500">Nenhum cliente encontrado</div>
                        )}
                      </div>
                    )}
                  </div>

                  {form.selectedClients.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 pt-1" data-testid="selected-clients-chips">
                      {form.selectedClients.map((c) => (
                        <span
                          key={c.login}
                          className="inline-flex items-center gap-1 bg-sky-500/15 text-sky-300 border border-sky-500/30 rounded-full pl-2.5 pr-1 py-1 text-xs"
                        >
                          {c.nome}
                          <button
                            data-testid={`remove-client-${c.login}`}
                            onClick={() => removeClient(c.login)}
                            className="hover:bg-sky-500/20 rounded-full p-0.5"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <p className="text-[11px] text-slate-600">
                    {form.selectedClients.length} cliente(s) selecionado(s)
                  </p>
                </div>
              )}
            </div>

            <div>
              <Label className="text-slate-400 text-xs uppercase tracking-[0.15em]">Imagem (opcional)</Label>
              <p className="text-[11px] text-slate-600 mb-1">
                Enviada como foto no WhatsApp, com a mensagem abaixo como legenda. PNG/JPG, até 2MB.
              </p>
              <input ref={imageInputRef} type="file" accept="image/*" onChange={onImageSelected} className="hidden" />
              {form.image_data_uri ? (
                <div className="mt-1 flex items-center gap-3">
                  <img src={form.image_data_uri} alt="Preview" className="w-16 h-16 rounded-md object-cover border border-slate-700" />
                  <Button variant="outline" size="sm" onClick={pickImage} className="border-slate-700 hover:bg-slate-800 hover:text-white text-xs">
                    Trocar
                  </Button>
                  <button data-testid="campaign-image-remove" onClick={removeImage} className="text-slate-400 hover:text-red-400 p-1">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <Button
                  data-testid="campaign-image-upload-btn"
                  variant="outline"
                  onClick={pickImage}
                  className="mt-1 border-slate-700 hover:bg-slate-800 hover:text-white text-xs"
                >
                  <Upload className="w-3.5 h-3.5 mr-1.5" /> Escolher imagem
                </Button>
              )}
            </div>

            <div>
              <Label className="text-slate-400 text-xs uppercase tracking-[0.15em]">
                Mensagem {form.image_data_uri ? "(legenda da imagem)" : ""}
              </Label>
              <p className="text-[11px] text-slate-600 mb-1">
                Variáveis: <span className="font-mono-jb text-emerald-300">{"{primeiro_nome} {nome}"}</span>
              </p>
              <Textarea
                data-testid="campaign-message-input"
                value={form.message}
                onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                rows={5}
                placeholder="Olá {primeiro_nome}! Nesta sexta teremos manutenção programada das 22h às 00h..."
                className="bg-slate-950 border-slate-800 text-slate-200 text-sm"
              />
            </div>

            <div>
              <Label className="text-slate-400 text-xs uppercase tracking-[0.15em]">Envio</Label>
              <Select value={form.schedule_type} onValueChange={(v) => setForm((f) => ({ ...f, schedule_type: v }))}>
                <SelectTrigger data-testid="campaign-schedule-type" className="mt-1 bg-slate-950 border-slate-800 text-white h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700 text-slate-200">
                  <SelectItem value="once">Uma vez, numa data específica</SelectItem>
                  <SelectItem value="recurring">Recorrente (repete sozinha)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {form.schedule_type === "once" ? (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-slate-400 text-xs uppercase tracking-[0.15em]">Data</Label>
                  <Input
                    data-testid="campaign-send-date"
                    type="date"
                    value={form.send_at_date}
                    onChange={(e) => setForm((f) => ({ ...f, send_at_date: e.target.value }))}
                    className="mt-1 bg-slate-950 border-slate-800 text-white"
                  />
                </div>
                <div>
                  <Label className="text-slate-400 text-xs uppercase tracking-[0.15em]">Hora</Label>
                  <Input
                    data-testid="campaign-send-time"
                    type="time"
                    value={form.send_at_time}
                    onChange={(e) => setForm((f) => ({ ...f, send_at_time: e.target.value }))}
                    className="mt-1 bg-slate-950 border-slate-800 text-white"
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-3 border border-slate-800 rounded-md p-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-slate-400 text-xs uppercase tracking-[0.15em]">Repete</Label>
                    <Select value={form.recurrence_freq} onValueChange={(v) => setForm((f) => ({ ...f, recurrence_freq: v }))}>
                      <SelectTrigger data-testid="campaign-recurrence-freq" className="mt-1 bg-slate-950 border-slate-800 text-white h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-700 text-slate-200">
                        <SelectItem value="daily">Todo dia</SelectItem>
                        <SelectItem value="weekly">Toda semana</SelectItem>
                        <SelectItem value="monthly">Todo mês</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-slate-400 text-xs uppercase tracking-[0.15em]">Horário</Label>
                    <Input
                      data-testid="campaign-recurrence-time"
                      type="time"
                      value={form.recurrence_time}
                      onChange={(e) => setForm((f) => ({ ...f, recurrence_time: e.target.value }))}
                      className="mt-1 bg-slate-950 border-slate-800 text-white"
                    />
                  </div>
                </div>
                {form.recurrence_freq === "weekly" && (
                  <div>
                    <Label className="text-slate-400 text-xs uppercase tracking-[0.15em]">Dia da semana</Label>
                    <Select value={String(form.recurrence_weekday)} onValueChange={(v) => setForm((f) => ({ ...f, recurrence_weekday: v }))}>
                      <SelectTrigger data-testid="campaign-recurrence-weekday" className="mt-1 bg-slate-950 border-slate-800 text-white h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-700 text-slate-200">
                        {WEEKDAYS.map((w, i) => (
                          <SelectItem key={i} value={String(i)}>{w}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {form.recurrence_freq === "monthly" && (
                  <div>
                    <Label className="text-slate-400 text-xs uppercase tracking-[0.15em]">Dia do mês</Label>
                    <Input
                      data-testid="campaign-recurrence-day"
                      type="number" min={1} max={28}
                      value={form.recurrence_day_of_month}
                      onChange={(e) => setForm((f) => ({ ...f, recurrence_day_of_month: e.target.value }))}
                      className="mt-1 bg-slate-950 border-slate-800 text-white w-24"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setFormOpen(false)} className="border-slate-700 hover:bg-slate-800 hover:text-white">
              Cancelar
            </Button>
            <Button data-testid="campaign-submit-btn" onClick={submit} disabled={saving} className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold">
              {saving ? "Salvando…" : editing ? "Salvar alterações" : "Agendar campanha"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
